<script>
import { TZDate, tz } from '@date-fns/tz'
import formEmailSignature from '@domains/clients/components/modalEmailSignature/formEmailSignature.vue'
import { fetchMarketStatus, fetchPTAX } from '@domains/dashboard/api'
import { fetchFinancialInstitutions } from '@domains/super-admins/financial-institutions/api'
import toggleSwitchMenu from '@shared/components/_toggleSwitchMenu.vue'
import { theme } from '@shared/composables/useGlobalConfig'
import { useOperationMonitor } from '@shared/composables/useOperationMonitor'
import { fetchNatureType, fetchCountriesBanks, fetchBanks } from '@shared/services/configs'
import { handleGlobalError } from '@shared/services/errorHandler'
import { fetchExchangeRate } from '@shared/services/ms'
import { useWebSocket } from '@shared/services/useWebSocket'
import { useAuthToken } from '@shared/stores/authToken.js'
import { useCache } from '@shared/stores/cache.js'
import { useConfig } from '@shared/stores/config.js'
import { useWss } from '@shared/stores/wss.js'
import { loadAsset } from '@shared/utils/loadAsset.ts'
import { format } from 'date-fns'
import { ref, onMounted, watch, toRefs, computed } from 'vue'

import router from '@/router'

export default {
  name: 'appStatusBar',

  components: {
    toggleSwitchMenu,
    formEmailSignature
  },

  setup() {
    const authentication = useAuthToken()
    useOperationMonitor()

    const { startWebSocketConnections, disconnectWebSocketConnections } = useWebSocket()
    const { getExchangeRate, isMarketClosed, getPTAX } = toRefs(useWss())

    const isStatusMarketClosed = ref(isMarketClosed.value)
    const variationInfo = ref('')
    const isPositive = ref(false)
    const variationNow = ref('')
    const dateInfo = ref('--:--:--')

    const actionLogout = async () => {
      await disconnectWebSocketConnections()
      authentication.resetState()
      setTimeout(() => {
        router.push('/login')
      }, 300)
    }

    const loadMarketStatus = async () => {
      try {
        const response = await fetchMarketStatus()
        isStatusMarketClosed.value = response.data.payload
        useWss().market = response.data.payload
      } catch (e) {
        handleGlobalError(e, 'fetchMarketStatus')
      }
    }

    watch(isMarketClosed, (newVal) => {
      isStatusMarketClosed.value = newVal
    })

    const loadCurrencyInfo = async () => {
      try {
        const response = await fetchExchangeRate(config.favoriteCurrency)
        formatValueBuy(response.data.payload.buyPrice)
        formatValueSell(response.data.payload.sellPrice)
        variationInfo.value = Number(response.data.payload.openChange).toFixed(2)
        variationNow.value = response.data.payload.currentPrice
        dateInfo.value = format(new Date(response.data.payload.lastRefreshed), 'HH:mm:ss')
        isPositive.value = variationInfo.value >= 0
      } catch (e) {
        handleGlobalError(e, 'fetchExchangeRate')
      }
    }

    watch(
      getExchangeRate,
      (newVal) => {
        if (newVal != null && newVal[config.favoriteCurrency] != null) {
          formatCurrencyType(newVal[config.favoriteCurrency])
        }
      },
      { deep: true }
    )

    const formatCurrencyType = (value) => {
      const splits = value.split('|')
      formatValueBuy(splits[7])
      formatValueSell(splits[6])
      variationInfo.value = Number(splits[8]).toFixed(2)
      variationNow.value = splits[4]
      dateInfo.value = format(new Date(splits[5]), 'HH:mm:ss')
      isPositive.value = variationInfo.value >= 0
    }

    const intBuy = ref('0')
    const decimalBuy = ref('0000')

    const formatValueBuy = (value) => {
      const valStr = parseFloat(value.toString()).toFixed(4)
      const valArray = valStr.split('.')
      intBuy.value = valArray[0]
      decimalBuy.value = valArray[1].slice(0, 4)
    }

    const intSell = ref('0')
    const decimalSell = ref('0000')

    const formatValueSell = (value) => {
      const valStr = parseFloat(value.toString()).toFixed(4)
      const valArray = valStr.split('.')
      intSell.value = valArray[0]
      decimalSell.value = valArray[1].slice(0, 4)
    }

    const noPTAXvailable = ['AED', 'CNY', 'USDC', 'USDT']

    const ptax = ref({})

    const loadPTAXInfo = async () => {
      if (noPTAXvailable.includes(config.favoriteCurrency)) {
        ptax.value = {}
        return
      }
      try {
        const response = await fetchPTAX(config.favoriteCurrency)
        formatValuePTAX(response.data.payload)
        useWss().PTAX = response.data.payload
      } catch (e) {
        handleGlobalError(e, 'fetchPtax')
      }
    }

    const formatValuePTAX = (value) => {
      for (const key of ['ptax10', 'ptax11', 'ptax12', 'ptax13', 'ptax']) {
        if (value[key] == null) continue
        ptax.value[key] = Number(value[key])
      }

      const defaultDate = new Date()
      const createdAt = value.createdAt ? new Date(`${value.createdAt}T00:00:00Z`) : defaultDate
      const d = new TZDate(createdAt, 'UTC')
      ptax.value.date = format(d, 'dd/MM', { in: tz('UTC') })
    }

    watch(getPTAX, (newVal) => {
      if (newVal.currency !== config.favoriteCurrency) return
      formatValuePTAX(newVal)
    })

    const formatPTAXValue = (value) => {
      return value?.toFixed(4).toString().replace('.', ',') || '0,0000'
    }

    const ptaxEntries = computed(() => {
      const entries = [
        { label: '10h', key: 'ptax10' },
        { label: '11h', key: 'ptax11' },
        { label: '12h', key: 'ptax12' },
        { label: '13h', key: 'ptax13' }
      ].map((entry) => ({
        ...entry,
        active: ptax.value[entry.key] !== undefined && ptax.value[entry.key] !== 0,
        value: formatPTAXValue(ptax.value[entry.key]),
        showDate: false
      }))

      entries.unshift({
        label: `PTAX ${ptax.value.date || '00/00'}`,
        key: 'ptax',
        active: ptax.value.ptax !== undefined && ptax.value.ptax !== 0,
        value: formatPTAXValue(ptax.value.ptax),
        showDate: true
      })

      return entries
    })

    const loadNatures = async () => {
      try {
        const config = {
          shouldIncludeDisabled: true
        }
        const response = await fetchNatureType(config)
        useCache().natureType = response.data.payload
      } catch (e) {
        handleGlobalError(e, 'fetchNatureType')
      }
    }

    const loadFinancialInstitutions = async () => {
      try {
        const response = await fetchFinancialInstitutions()
        useCache().financialInstitutions = response.data.payload
      } catch (e) {
        handleGlobalError(e, 'fetchFinancialInstitutions')
      }
    }

    const loadCountriesBanks = async () => {
      try {
        const response = await fetchCountriesBanks()
        useCache().countriesBanks = response.data.payload
      } catch (e) {
        handleGlobalError(e, 'fetchCountriesBanks')
      }
    }

    const loadBanks = async () => {
      try {
        const response = await fetchBanks()
        useCache().banks = response.data.payload
      } catch (e) {
        handleGlobalError(e, 'fetchBanks')
      }
    }

    onMounted(async () => {
      if (getExchangeRate.value[config.favoriteCurrency] != null) {
        formatCurrencyType(getExchangeRate.value[config.favoriteCurrency])
      }
      if (getPTAX.value != null) {
        formatValuePTAX(getPTAX.value)
      }
      loadMarketStatus()
      loadNatures()
      loadFinancialInstitutions()
      loadCountriesBanks()
      await loadBanks()
      selectFavoriteCurrency(config.favoriteCurrency)
      await startWebSocketConnections()
    })

    const config = useConfig()

    const toggleDarkMode = () => {
      const htmlElement = document.documentElement
      const isDark = config.theme === theme.DARK

      htmlElement.classList.remove(isDark ? theme.DARK : theme.LIGHT)
      config.theme = isDark ? theme.LIGHT : theme.DARK
      htmlElement.classList.add(config.theme)
    }

    const toggleNotificationSound = () => {
      config.sound = !config.sound
    }

    const firstName = computed(() => {
      const name = authentication.getName
      return name ? name.split(' ')[0] : ''
    })

    const initials = computed(() => {
      const name = authentication.getName || ''
      const words = name.split(' ')
      const initials = words
        .filter((word) => word.length > 0)
        .slice(0, 2)
        .map((word) => word[0])
        .join('')

      return initials
    })

    const currencyNames = {
      USD: 'DÓLAR',
      GBP: 'LIBRA',
      EUR: 'EURO',
      CAD: 'DÓLAR CANADENSE',
      CHF: 'FRANCO SUÍÇO',
      JPY: 'IENE',
      AUD: 'DÓLAR AUSTRALIANO',
      CNY: 'YUAN CHINÊS',
      AED: 'DIRHAM',
      SEK: 'COROA SUECA',
      USDC: 'USD COIN (USDC)',
      USDT: 'TETHER (USDT)'
    }

    const currencies = Object.keys(currencyNames)

    const selectFavoriteCurrency = (coin) => {
      config.favoriteCurrency = coin
      loadCurrencyInfo()
      loadPTAXInfo()
    }

    const title = ref('')
    const modalEnabled = ref(false)
    const loadingButton = ref(false)

    const openSignatureModal = () => {
      title.value = 'Criar assinatura'
      modalEnabled.value = true
    }

    const handleFormClose = () => {
      modalEnabled.value = false
    }

    return {
      actionLogout,
      toggleDarkMode,
      toggleNotificationSound,
      useConfig,
      isStatusMarketClosed,

      intBuy,
      decimalBuy,

      intSell,
      decimalSell,

      variationInfo,
      isPositive,
      dateInfo,

      ptaxEntries,
      loadAsset,

      initials,
      firstName,

      selectFavoriteCurrency,
      currencyNames,
      currencies,
      config,

      modalEnabled,
      title,
      handleFormClose,
      loadingButton,

      openSignatureModal
    }
  }
}
</script>

<template>
  <div class="row">
    <form-email-signature
      v-if="modalEnabled"
      :title="title"
      @onClose="handleFormClose"
      :loading="loadingButton"
    />

    <div class="col nav-bar-operation">
      <div class="d-flex align-items-center">
        <div class="flag">
          <img
            :src="loadAsset(`coins/${config.favoriteCurrency}.svg`)"
            class="img-responsive rounded"
            height="36"
            width="53"
            alt="flag"
            id="navbarDropdown"
            data-bs-toggle="dropdown"
            aria-expanded="false"
          />
          <p>{{ currencyNames[config.favoriteCurrency] }} <br />COMERCIAL</p>
          <ul class="dropdown-menu select-favorite-currency" aria-labelledby="navbarDropdown">
            <div v-for="currency in currencies" :key="currency">
              <li
                v-if="config.favoriteCurrency !== currency"
                @click="selectFavoriteCurrency(currency)"
              >
                <img
                  :src="loadAsset(`coins/${currency}.svg`)"
                  class="img-responsive rounded"
                  height="36"
                  width="53"
                  alt="flag"
                />
              </li>
            </div>
          </ul>
        </div>
        <div class="brokerage">
          <p>
            COMPRA <br />
            <span>{{ intBuy }},{{ decimalBuy }}</span>
          </p>
          <p>
            VENDA <br />
            <span>{{ intSell }},{{ decimalSell }}</span>
          </p>
          <p>
            VARIAÇÃO <br />
            <span
              :class="{
                bull_market: isPositive,
                bear_market: !isPositive
              }"
            >
              {{ variationInfo }}%
              <i
                class="bi"
                :class="{
                  'bi-caret-up-fill': isPositive,
                  'bi-caret-down-fill': !isPositive
                }"
              ></i>
            </span>
          </p>
          <p>
            ATUALIZADO <br />
            <span>{{ dateInfo }}</span>
          </p>
        </div>
        <div class="ptax-box">
          <p v-for="entry in ptaxEntries" :key="entry.key" :class="{ active: entry.active }">
            {{ entry.label }} <br />
            <span>{{ entry.value }}</span>
          </p>
        </div>
        <div class="status">
          <p>
            STATUS:
            <span
              class="market"
              :class="{
                open_market: isStatusMarketClosed,
                close_market: !isStatusMarketClosed
              }"
            >
              {{ isStatusMarketClosed ? 'ABERTO' : 'FECHADO' }} </span
            ><br />
            <span>09h00 às 16h30</span>
          </p>
        </div>

        <li class="dropdown ms-auto">
          <a
            class="dropdown-toggle"
            href="#"
            id="navbarDropdown"
            role="button"
            data-bs-toggle="dropdown"
            aria-expanded="false"
          >
            <div>
              {{ initials }}
            </div>
            {{ firstName }}
          </a>
          <ul class="dropdown-menu" aria-labelledby="navbarDropdown">
            <li class="radio-dropdown-menu">
              <toggle-switch-menu
                id="sound"
                name="sound"
                :isChecked="useConfig().sound"
                label="Sons"
                :onToggle="toggleNotificationSound"
              />
            </li>

            <li class="radio-dropdown-menu">
              <toggle-switch-menu
                id="theme"
                name="theme"
                :isChecked="useConfig().theme === 'dark'"
                label="Dark Mode"
                :onToggle="toggleDarkMode"
              />
            </li>

            <li class="radio-dropdown-menu" @click="openSignatureModal">Assinatura de e-mail</li>

            <li class="radio-dropdown-menu" @click="actionLogout">
              <i class="bi bi-box-arrow-right"></i>
              Deslogar
            </li>
          </ul>
        </li>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.nav-bar-operation {
  > div {
    background-color: #131517;
    padding: 9px 22px;
    border-radius: 11px;
    margin-top: 24px;

    .flag {
      cursor: pointer;

      > img {
        float: left;
      }

      p {
        float: left;
        line-height: 17px;
        margin-bottom: 0;
        padding-left: 10px;
        font-size: 12px;
        font-weight: 500;
        color: #b7b7b7;
      }

      .select-favorite-currency {
        background-color: #131517;
        margin-left: -8px !important;
        min-width: 10px;
        padding: 0;

        li {
          padding: 8px;

          img {
            transition: all 300ms linear;
          }

          &:hover {
            background-color: transparent;

            img {
              transform: scale(1.05);
            }
          }
        }
      }
    }

    .brokerage {
      p {
        float: left;
        line-height: 17px;
        margin-bottom: 0;
        padding-left: 18px;
        font-size: 12px;
        font-weight: 500;
        color: #b7b7b7;

        span {
          color: #ffffff;

          &.bull_market {
            color: #0bdd78;
          }

          &.bear_market {
            color: #dd0b0b;
          }
        }
      }
    }

    .ptax-box {
      background-color: #2c272e;
      border-radius: 11px;
      padding: 15px 30px;
      margin: 0 30px;

      p {
        float: left;
        line-height: 17px;
        margin-bottom: 0;
        padding-left: 18px;
        font-size: 12px;
        font-weight: 500;
        color: #b7b7b7;

        &:first-child {
          padding-left: 0;
        }

        span {
          color: #484646;
        }

        &.active {
          span {
            color: #ffffff;
          }
        }
      }
    }

    .status {
      p {
        float: left;
        line-height: 17px;
        margin-bottom: 0;
        font-size: 12px;
        font-weight: 500;
        color: #b7b7b7;

        span {
          color: #ffffff;

          &.market {
            border-radius: 5px;
            padding: 0 5px;
            color: #000000;
            background-color: #757575;
          }

          &.open_market {
            border-radius: 5px;
            padding: 0 5px;
            color: #000000;
            background-color: #0bdd78;
          }

          &.close_market {
            border-radius: 5px;
            padding: 0 5px;
            color: #ffffff;
            background-color: #dd0b0b;
          }
        }
      }
    }

    .dropdown {
      .dropdown-toggle {
        padding: 10px 0px;
        color: #ffffff;
        img {
          margin-right: 14px;
        }
      }
    }
  }

  .brokerage {
    p {
      float: left;
      line-height: 17px;
      margin-bottom: 0;
      padding-left: 18px;
      font-size: 12px;
      font-weight: 500;
      color: #b7b7b7;

      span {
        color: #ffffff;

        &.bull_market {
          color: #0bdd78;
        }

        &.bear_market {
          color: #dd0b0b;
        }
      }
    }
  }

  .ptax-box {
    background-color: #2c272e;
    border-radius: 11px;
    padding: 15px 30px;
    margin: 0 30px;

    p {
      float: left;
      line-height: 17px;
      margin-bottom: 0;
      padding-left: 18px;
      font-size: 12px;
      font-weight: 500;
      color: #b7b7b7;

      &:first-child {
        padding-left: 0;
      }

      span {
        color: #ffffff;
      }
    }
  }

  .status {
    p {
      float: left;
      line-height: 17px;
      margin-bottom: 0;
      font-size: 12px;
      font-weight: 500;
      color: #b7b7b7;

      span {
        color: #ffffff;

        &.open_market {
          border-radius: 5px;
          padding: 0 5px;
          color: #000000;
          background-color: #0bdd78;
        }

        &.close_market {
          border-radius: 5px;
          padding: 0 5px;
          color: #ffffff;
          background-color: #dd0b0b;
        }
      }
    }
  }

  .dropdown {
    .dropdown-toggle {
      font-size: 14px;
      color: #ffffff;
      div {
        margin-right: 4px;
        width: 28px;
        height: 28px;
        background-color: #2e3034;
        color: #ffffff;
        padding-top: 6px;
        padding-left: 1px;
        border-radius: 20px;
        text-align: center;
        display: inline-grid;
        font-size: 12px;
      }
      img {
        margin-right: 14px;
      }
    }
  }

  .dropdown-menu {
    font-size: 14px;

    .dropdown-item {
      padding: 8px 15px;

      &:hover {
        color: #000 !important;
        background-color: #ffffff;
      }

      &:focus {
        color: #000 !important;
        background-color: #ffffff;
      }
    }

    .radio-dropdown-menu {
      padding: 8px 15px;
      cursor: pointer;
    }

    li {
      &:hover {
        background-color: #eeeeee;
      }
    }
  }
}
</style>
